import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "react-jss";
import { kebabCase } from "../../../utils/lodash";
import useStyles from "./ChoiceListItemRenderer.styles";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import ChoiceOpenEndRenderer from "./ChoiceOpenEndRenderer";
import { VariableModel } from "../../../models/VariableModel";
import { QuestionType, isChoiceList } from "../../../models/enums/QuestionType";
import { ChoiceRef } from "../../choices/BaseChoice";
import useQuestion from "../../../hooks/useQuestion";
import ChoiceLabelRenderer from "./ChoiceLabelRenderer";
import { ButtonChoice } from "../../shared/ButtonChoice";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString, getSettingNumber } from "../../../models/utilities/settingUtilities";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";
import { hasOpenEnd } from "../../../models/utilities/choiceUtilities";
import { StoreState } from "../../../models/states/StoreState";
import { DefaultFocusStatus } from "../../../models/states/DefaultFocusStatus";
import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { setDefaultFocusStatus } from "../../../redux/actions/index";

interface Props extends RendererProps {
    selected?: boolean;
    variable: VariableModel;
    isFirst?: boolean;
    isLast?: boolean;
    name?: string;
    gridRendering?: boolean;
    openEnd?: string;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    choice?: ChoiceModel;
    horizontal?: boolean;
    controlType?: any;
    disabled?: boolean;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
}

const Choice = React.forwardRef(function ChoiceImpl(props: Props, ref: ChoiceRef) {
    const {
        choice,
        controlType,
        openEnd,
        isFirst,
        isLast,
        disabled,
        selected,
        variable,
        onOpenEndBlur,
        onOpenEndChange,
        language,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, selected });

    return (
        <>
            <div className={classes.choiceContainer}>
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={ref}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            </div>
            <ChoiceOpenEndRenderer
                {...getRendererProps(props)}
                choice={choice}
                isSelected={selected}
                name={choice.elementId}
                label={removeHtmlTags(translate(choice, "TEXT", language))}
                value={openEnd}
                onOpenEndBlur={onOpenEndBlur}
                onOpenEndChange={onOpenEndChange}
                size={getSettingString(choice, "OpenEndSize", "Full")}
                lines={getSettingNumber(choice, "OpenEndLines", 2)}
            />
        </>
    );
});

export default function ChoiceListItemRenderer(props: Props) {
    const {
        choice,
        controlType,
        isFirst,
        isLast,
        disabled,
        openEnd,
        onOpenEndBlur,
        onOpenEndChange,
        gridRendering,
        selected,
        variable,
        horizontal,
        onAfterSelection,
    } = props;
    const choiceRef: ChoiceRef = React.useRef();
    const question = useQuestion();
    const theme = useTheme();
    const classes = useStyles({ theme, selected, horizontal });
    const choiceLabel = removeHtmlTags(useTranslatable(choice, "TEXT"));
    const dispatch = useDispatch();
    const defaultFocusStatus = useSelector(
        (state: StoreState) => state.defaultFocusStatus,
    ) as DefaultFocusStatus;

    if (!choice.value) {
        return (
            <div
                id={`${choice.elementId}_INPUT`}
                className={classes.choiceHeader}
                aria-labelledby={`${choice.elementId}_LABEL`}
            >
                <ChoiceLabelRenderer choice={choice} gridRendering={gridRendering} />
            </div>
        );
    }

    if (question.type === QuestionType.ImageSelector) {
        return <Choice ref={choiceRef} {...props} />;
    }

    const type = isChoiceList(question.type) ? question.type : QuestionType.RadioButton;

    function onSelect() {
        if (choiceRef.current) {
            choiceRef.current.select();

            if (onAfterSelection) {
                onAfterSelection();
            }
        }
    }

    function getCurrentElementFromStore() {
        const currentButtonChoiceId = `${choice.elementId}_INPUT`;
        const currentElement = defaultFocusStatus.statuses.find((element) => {
            return (
                element.variableId === variable.id && element.elementId === currentButtonChoiceId
            );
        });

        return currentElement;
    }

    function findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ) {
        const index = defaultFocusStatus.statuses.indexOf(currentElement);

        for (let i = 0; i < defaultFocusStatus.statuses.length; i++) {
            const elementFocusStatus = defaultFocusStatus.statuses[i];
            const indexCondition =
                eventKey === "ArrowDown" || eventKey === "ArrowRight" ? i > index : i < index;

            if (
                elementFocusStatus.variableId === variable.id &&
                elementFocusStatus.elementId !== currentElement.elementId &&
                indexCondition
            )
                return elementFocusStatus;
        }

        return null;
    }

    function findNextFocusableElementWithinNextVariable(currentElement: DefaultFocusStatusModel) {
        const index = defaultFocusStatus.statuses.indexOf(currentElement);

        for (let i = index + 1, len = defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.variableId !== 0 &&
                elementFocusStatus.variableId !== variable.id
            )
                return elementFocusStatus;
        }

        return null;
    }

    function updateNewFocusStatus(elementWithFocus: DefaultFocusStatusModel) {
        // Reset the focus status
        defaultFocusStatus.statuses.forEach((element) => {
            // eslint-disable-next-line no-param-reassign
            element.focusStatus = false;
        });

        // Update the new focus status
        const index = defaultFocusStatus.statuses.indexOf(elementWithFocus);
        if (index) {
            defaultFocusStatus.statuses[index].focusStatus = true;
            dispatch(setDefaultFocusStatus(defaultFocusStatus));
        }
    }

    function removeFocusForRemainingElementsOfLastQuestion(
        currentElement: DefaultFocusStatusModel,
    ) {
        const elementsToUpdate: DefaultFocusStatusModel[] = [];
        const index = defaultFocusStatus.statuses.indexOf(currentElement);

        for (let i = index + 1; i < defaultFocusStatus.statuses.length; i++) {
            const elementFocusStatus = defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.variableId !== 0 &&
                elementFocusStatus.variableId === currentElement.variableId &&
                elementFocusStatus.elementId !== currentElement.elementId
            )
                elementsToUpdate.push(elementFocusStatus);
        }

        elementsToUpdate.forEach((element) => {
            if (element.elementRef && element.elementRef.current)
                element.elementRef.current.setAttribute("tabindex", "-1");
        });
    }

    function handleKeyPressEvent(event: React.KeyboardEvent<HTMLDivElement>) {
        if (
            defaultFocusStatus &&
            defaultFocusStatus.statuses &&
            defaultFocusStatus.statuses.length > 1
        ) {
            const currentElementInfo = getCurrentElementFromStore();
            if (currentElementInfo) {
                let nextFocusableElementInfo;

                switch (event.key) {
                    case "ArrowLeft":
                    case "ArrowRight":
                    case "ArrowUp":
                    case "ArrowDown":
                        nextFocusableElementInfo = findNextFocusableElementWithinSameVariable(
                            currentElementInfo,
                            event.key,
                        );
                        break;
                    case "Tab":
                        nextFocusableElementInfo = findNextFocusableElementWithinNextVariable(
                            currentElementInfo,
                        );
                        break;
                    default:
                        nextFocusableElementInfo = null;
                }

                if (nextFocusableElementInfo) {
                    // Disabling the below code on a temporary basis as per the ticket DEV-78236 since as of now this behaviour only implemented
                    // for radio button question type which is making the respondents inconvience when we have questions
                    // other than radio button type in the page.

                    // const elementToFocus = nextFocusableElementInfo.elementRef.current;
                    // if (elementToFocus.scrollIntoView) {
                    //     elementToFocus.scrollIntoView(true);
                    // }
                    // elementToFocus.focus();
                    // event.preventDefault();

                    updateNewFocusStatus(nextFocusableElementInfo);
                } else {
                    removeFocusForRemainingElementsOfLastQuestion(currentElementInfo);
                }
            }
        }
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.key === " ") {
            onSelect();
            event.preventDefault();
        } else if (
            event.key === "ArrowUp" ||
            event.key === "ArrowDown" ||
            event.key === "ArrowRight" ||
            event.key === "ArrowLeft" ||
            event.key === "Tab"
        ) {
            handleKeyPressEvent(event);
        }
    }

    const openEndSize = getSettingString(choice, "OpenEndSize", "Medium");
    const carrouselChoiceBackgound = getSettingString(choice, "Color");

    return (
        <ButtonChoice
            id={`${choice.elementId}_INPUT`}
            choiceElementId={`${choice.elementId}`}
            type={type === QuestionType.RadioButton ? "radio" : "checkbox"}
            label={choiceLabel}
            onKeyDown={onKeyDown}
            onSelect={onSelect}
            hasOpenEnd={hasOpenEnd(choice)}
            openEndSize={openEndSize}
            selected={selected}
            className={kebabCase(QuestionType[type])}
            horizontal={horizontal}
            renderChoice={() => (
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={choiceRef}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            )}
            renderAfter={() => (
                <ChoiceOpenEndRenderer
                    {...getRendererProps(props)}
                    choice={choice}
                    isSelected={selected}
                    name={choice.elementId}
                    label={choiceLabel}
                    value={openEnd}
                    onOpenEndBlur={onOpenEndBlur}
                    onOpenEndChange={onOpenEndChange}
                    size={openEndSize}
                    lines={getSettingNumber(choice, "OpenEndLines", 2)}
                />
            )}
            carrouselChoiceBackgound={carrouselChoiceBackgound}
            variable={variable}
        />
    );
}
